import { DateTimeStr } from '@mi-front/core';
import { create } from 'zustand';
import {
  TranscriptionBlockData,
  TranscriptionSegmentData,
  TranscriptionWordData,
} from '../lib/transcription.types';

type TranscriptionInteractionStateInput = {
  followPlayback: boolean;
  showWordEfficiency: boolean;
  matchWordIds: Set<string>;
  isEditMode: boolean;
  editingWordId: string | null;
  editingDraftText: string;
  isWordSaving: boolean;
  wordSaveError: string | null;
};

type TranscriptionInteractionHandlersInput = {
  onEditModeClick: () => void;
  onSyncIconClick: () => void;
  onWordEfficiencyClick: () => void;
  onEditingDraftChange: (value: string) => void;
  onWordEditStart: (wordId: string, text: string) => void;
  onWordEditSave: () => void;
  onWordEditCancel: () => void;
  onWordClick: (timeSeconds: number, chunkTime: DateTimeStr) => void;
  onSeekSegment: (block: TranscriptionBlockData) => void;
  isBlockActive: (segment: TranscriptionSegmentData) => boolean;
  isWordHighlighted: (word: TranscriptionWordData, blockActive: boolean) => boolean;
};

type TranscriptionInteractionsStore = TranscriptionInteractionStateInput &
  TranscriptionInteractionHandlersInput & {
    enableFollowPlayback: () => void;
    disableFollowPlayback: () => void;
    setIsEditMode: (enabled: boolean) => void;
    setShowWordEfficiency: (enabled: boolean) => void;
    setInteractionState: (state: Partial<TranscriptionInteractionStateInput>) => void;
    setInteractionHandlers: (handlers: TranscriptionInteractionHandlersInput) => void;
    reset: () => void;
  };

const initialState: TranscriptionInteractionStateInput = {
  followPlayback: true,
  showWordEfficiency: false,
  matchWordIds: new Set<string>(),
  isEditMode: false,
  editingWordId: null,
  editingDraftText: '',
  isWordSaving: false,
  wordSaveError: null,
};

const noop = () => undefined;
const noopIsBlockActive = () => false;
const noopIsWordHighlighted = () => false;

const initialHandlers: TranscriptionInteractionHandlersInput = {
  onEditModeClick: noop,
  onSyncIconClick: noop,
  onWordEfficiencyClick: noop,
  onEditingDraftChange: noop,
  onWordEditStart: noop,
  onWordEditSave: noop,
  onWordEditCancel: noop,
  onWordClick: noop,
  onSeekSegment: noop,
  isBlockActive: noopIsBlockActive,
  isWordHighlighted: noopIsWordHighlighted,
};

/**
 * Shared interaction store for the transcription panel.
 * It centralizes editing/playback/search interaction state to avoid prop drilling between list levels.
 */
export const useTranscriptionInteractionsStore = create<TranscriptionInteractionsStore>()(
  (set) => ({
    ...initialState,
    ...initialHandlers,
    enableFollowPlayback: () => {
      set({ followPlayback: true });
    },
    disableFollowPlayback: () => {
      set({ followPlayback: false });
    },
    setIsEditMode: (enabled) => {
      set({ isEditMode: enabled });
    },
    setShowWordEfficiency: (enabled) => {
      set({ showWordEfficiency: enabled });
    },
    setInteractionState: (state) => {
      set(state);
    },
    setInteractionHandlers: (handlers) => {
      set(handlers);
    },
    reset: () => {
      set({
        ...initialState,
        ...initialHandlers,
      });
    },
  }),
);
